import { Component, OnInit, Inject, AfterViewInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { WhatsappService } from '../../shared/services/whatsapp.service';
import { EvolutionInstanceService } from '../../shared/services/evolution-instance.service';
import { AppConfirmService } from '../../shared/services/app-confirm/app-confirm.service';
import { HttpEventType } from '@angular/common/http';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ArtePersonalizadaService, ModeloArte } from '../../shared/services/arte-personalizada.service';
import { ArtePersonalizarDialogComponent } from './arte-personalizar-dialog/arte-personalizar-dialog.component';
import { warn } from 'console';
import { WhatsAppConfigService } from '../utilitario/whatsapp-config/whatsapp-config.service';
import { JwtAuthService } from '../../shared/services/auth/jwt-auth.service';
import { AppLoaderService } from '../../shared/services/app-loader/app-loader.service';
import { TxtAddService } from '../../shared/services/txtAdd.service';
import { CadastroService } from '../../shared/services/cadastro.service';
import { TxtAdd } from '../../shared/interfaces/txtAdd';
import { Subject } from 'rxjs';
import { debounceTime, switchMap } from 'rxjs/operators';

@Component({
  selector: 'app-whatsapp-edit',
  templateUrl: './whatsapp-edit.component.html',
  styleUrls: ['./whatsapp-edit.component.css']
})
export class WhatsappEditComponent implements OnInit, AfterViewInit {
  form: FormGroup;
  attachments: File[] = [];
  sending = false;
  instanceName: string | null = null;

  // Histórico e retry
  sendHistory: Array<{
    timestamp: Date;
    status: 'success' | 'error' | 'pending';
    recipientCount: number;
    successCount?: number;
    errorCount?: number;
    errors?: any[];
  }> = [];
  lastErrors: any[] = [];
  showRetryOption = false;
  retrying = false;

  // Lookup Texto Adicional
  showTxtAddLookup = false;
  txtAddResults: TxtAdd[] = [];
  txtAddSearch = '';
  txtAddLoading = false;
  private txtAddSearch$ = new Subject<string>();
  private static readonly TXTADD_SEARCH_KEY = 'whatsapp_txtadd_last_search';

  // Personalização da arte anexada (mala direta com nome/cupom/vencimento)
  /** fone normalizado -> nome do cadastro, enviado por quem abriu o diálogo. */
  private mapaNomes = new Map<string, string>();
  /** Lista na ordem em que veio da tela de origem — usada quando o telefone não casa. */
  private destinatariosOriginais: Array<{ fone: string; nome: string }> = [];
  /** Calibração da arte; enquanto for null o envio segue o fluxo comum. */
  modeloArte: ModeloArte | null = null;
  /** Fica no .ts porque chave literal no template é lida como bloco ICU pelo Angular. */
  readonly dicaPersonalizacao = 'Substitui {NOME}, {NOME:20} e {VCTO} na imagem, um envio por destinatário.';

  // Lookup Cadastro (busca do celular do destinatário)
  showCadastroLookup = false;
  cadastroResults: any[] = [];
  cadastroSearch = '';
  cadastroLoading = false;
  private cadastroSearch$ = new Subject<string>();
  private static readonly CADASTRO_SEARCH_KEY = 'whatsapp_cadastro_last_search';

  constructor(
    private fb: FormBuilder,
    private whatsappConfigService: WhatsAppConfigService,
    private confirmService: AppConfirmService,
    private jwtAuth: JwtAuthService,
    private whatsappService: WhatsappService,
    private loader: AppLoaderService,
    private txtAddService: TxtAddService,
    private cadastroService: CadastroService,
    private arteService: ArtePersonalizadaService,
    private dialog: MatDialog,
    public dialogRef: MatDialogRef<WhatsappEditComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    const toValidators = (data && data.to) ? [] : [Validators.required];
    this.form = this.fb.group({
      to: ['', toValidators],
      message: ['', [Validators.required]]
    });
  }

  ngOnInit(): void {
    this.txtAddSearch$.pipe(
      debounceTime(400),
      switchMap(term => {
        this.txtAddLoading = true;
        return term
          ? this.txtAddService.getTxtAddPageSearch(1, 20, term)
          : this.txtAddService.getTxtAddPage(1, 20);
      })
    ).subscribe(page => {
      this.txtAddResults = page.content || [];
      this.txtAddLoading = false;
    }, () => this.txtAddLoading = false);

    this.cadastroSearch$.pipe(
      debounceTime(400),
      switchMap(term => {
        this.cadastroLoading = true;
        return this.cadastroService.getCadastroPageSearch(1, 20, term || '');
      })
    ).subscribe((page: any) => {
      this.cadastroResults = page?.content || [];
      this.cadastroLoading = false;
    }, () => this.cadastroLoading = false);
  }

  ngAfterViewInit(): void {
    // preencher valores iniciais passados via MAT_DIALOG_DATA
    try {
      if (this.data) {
        if (this.data.to) {
          this.form.controls['to'].setValue(this.data.to);
        }
        if (this.data.message) {
          this.form.controls['message'].setValue(this.data.message);
        }
        // destinatários com nome: habilitam a personalização da arte
        if (Array.isArray(this.data.destinatarios)) {
          this.data.destinatarios.forEach((d: any) => {
            const nome = d && d.nome ? String(d.nome).trim() : '';
            const fone = d && d.fone ? String(d.fone) : '';
            if (!fone) {
              return;
            }
            // guarda TODOS (mesmo sem nome) para o índice acompanhar o campo "to"
            this.destinatariosOriginais.push({ fone, nome });
            if (nome) {
              this.chavesFone(fone).forEach(k => this.mapaNomes.set(k, nome));
            }
          });
          if (!this.mapaNomes.size) {
            console.warn('WhatsApp: nenhum destinatário com nome recebido', this.data.destinatarios);
          }
        }
        // aceitar arquivos passados como Blobs ou Files
        if (this.data.files && Array.isArray(this.data.files) && this.data.files.length > 0) {
          this.data.files.forEach((f: any, idx: number) => {
            if (f instanceof File) {
              this.attachments.push(f);
            } else if (f instanceof Blob) {
              const name = (f as any).name || (`attachment-${idx + 1}.pdf`);
              this.attachments.push(new File([f], name, { type: (f as any).type || 'application/pdf' }));
            } else if (f && f.blob && f.name) {
              this.attachments.push(new File([f.blob], f.name, { type: f.blob.type || 'application/pdf' }));
            }
          });
        }
      }
    } catch (e) {
      console.warn('Erro ao popular dados iniciais do WhatsApp dialog', e);
    }
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files) {
      return;
    }
    for (let i = 0; i < input.files.length; i++) {
      const file = input.files.item(i);
      if (file) {
        this.attachments.push(file);
      }
    }
    input.value = '';
  }

  removeAttachment(index: number): void {
    this.attachments.splice(index, 1);
    if (this.indiceArte() < 0) {
      // sem imagem anexada não há o que personalizar
      this.modeloArte = null;
    }
  }

  // ------------------------------------------------ personalização da arte

  /** Índice do primeiro anexo que é imagem — a arte da campanha. */
  private indiceArte(): number {
    return this.attachments.findIndex(f => this.arteService.ehImagem(f));
  }

  /**
   * Todas as formas pelas quais o mesmo telefone pode aparecer.
   * O campo do cadastro vem formatado ("(11) 98765-4321"), o envio normaliza
   * para "5511987654321", e ainda há cadastro com e sem o 9º dígito — casar por
   * uma única forma deixava o nome de fora e o texto caía no "Cliente".
   */
  private chavesFone(fone: string): string[] {
    const d = String(fone || '').replace(/\D/g, '');
    if (!d) {
      return [];
    }
    const chaves = new Set<string>([d]);
    [8, 9, 10, 11].forEach(n => {
      if (d.length >= n) {
        chaves.add(d.slice(-n));
      }
    });
    return Array.from(chaves);
  }

  private nomeDe(numero: string): string {
    const chaves = this.chavesFone(numero);
    for (const k of chaves) {
      const nome = this.mapaNomes.get(k);
      if (nome) {
        return nome;
      }
    }
    return '';
  }

  /**
   * Nome do destinatário na posição `i` do envio.
   * Se o telefone não casar (número editado à mão, formato inesperado), usa a
   * ordem em que a tela de origem mandou a lista — que é a mesma do campo "to".
   */
  private nomeNaPosicao(numero: string, i: number): string {
    const porFone = this.nomeDe(numero);
    if (porFone) {
      return porFone;
    }
    const original = this.destinatariosOriginais[i];
    if (original && this.destinatariosOriginais.length === this.totalDestinatarios) {
      return original.nome;
    }
    return '';
  }

  /** Quantos números o envio atual tem — base para o fallback por posição. */
  private totalDestinatarios = 0;

  /**
   * Resolve {NOME} e afins no texto da mensagem.
   * Vale para qualquer envio com destinatários nomeados — inclusive sem arte
   * anexada, como a mensagem de parabéns do filtro de aniversariantes.
   */
  private mensagemPara(numero: string, message: string, dataBase: Date, i: number): string {
    if (!this.mapaNomes.size || !message) {
      return message;
    }
    return this.arteService.resolver(message, {
      nome: this.nomeNaPosicao(numero, i) || 'Cliente',
      desconto: this.modeloArte ? this.modeloArte.desconto : 20,
      dataBase,
      prazoDiasUteis: this.modeloArte ? this.modeloArte.prazoDiasUteis : 15
    });
  }

  /** Só faz sentido oferecer personalização com arte anexada e nomes conhecidos. */
  get podePersonalizar(): boolean {
    return this.indiceArte() >= 0 && this.mapaNomes.size > 0;
  }

  /** Quantos destinatários chegaram com nome — mostrado na tela para diagnóstico. */
  get qtdeComNome(): number {
    return this.destinatariosOriginais.filter(d => !!d.nome).length;
  }

  /** Avisa quando há arte anexada mas nenhum nome veio junto: tudo sairia como "Cliente". */
  get semNomes(): boolean {
    return this.indiceArte() >= 0 && this.mapaNomes.size === 0;
  }

  get resumoPersonalizacao(): string {
    if (!this.modeloArte) {
      return '';
    }
    const qtd = this.modeloArte.campos.length;
    return `${qtd} ${qtd === 1 ? 'campo' : 'campos'} · ${this.modeloArte.desconto}% · ${this.modeloArte.prazoDiasUteis} dias úteis`;
  }

  personalizarArte(): void {
    const idx = this.indiceArte();
    if (idx < 0) {
      return;
    }
    const nomes = Array.from(this.mapaNomes.values());
    this.dialog.open(ArtePersonalizarDialogComponent, {
      width: '900px',
      maxWidth: '95vw',
      data: {
        arquivo: this.attachments[idx],
        nomes,
        modelo: this.modeloArte
      }
    }).afterClosed().subscribe((modelo: ModeloArte) => {
      if (modelo) {
        this.modeloArte = modelo;
      }
    });
  }

  removerPersonalizacao(): void {
    this.modeloArte = null;
  }

  /**
   * Extrair informações úteis do objeto de erro
   */
  private extractErrorInfo(err: any): any {
    if (err.type && err.userMessage) {
      return {
        type: err.type,
        message: err.message,
        userMessage: err.userMessage,
        status: err.status
      };
    }

    if (err instanceof Error) {
      return {
        type: 'GENERIC_ERROR',
        message: err.message,
        userMessage: err.message || 'Erro desconhecido',
        status: null
      };
    }

    if (typeof err === 'object') {
      return {
        type: err.type || err.error?.type || 'UNKNOWN',
        message: err.message || err.error?.message || 'Erro desconhecido',
        userMessage: err.userMessage || err.error?.userMessage || 'Erro desconhecido',
        status: err.status || err.error?.status || null
      };
    }

    return {
      type: 'UNKNOWN',
      message: String(err),
      userMessage: 'Erro desconhecido',
      status: null
    };
  }

  /**
   * Formatar resumo de erros de forma amigável ao usuário
   */
  private formatErrorSummary(errors: any[], successes: any[], total: number): string {
    const errorsByType: { [key: string]: any[] } = {};

    // Agrupar erros por tipo
    errors.forEach(err => {
      const errorObj = err.error?.originalError || err.error || err;
      const errorType = errorObj.type || 'UNKNOWN';
      const userMessage = errorObj.userMessage || errorObj.message || 'Erro desconhecido';

      if (!errorsByType[errorType]) {
        errorsByType[errorType] = [];
      }
      errorsByType[errorType].push({
        number: err.number,
        message: userMessage,
        step: err.step
      });
    });

    // Construir mensagem formatada
    let message = `Enviadas: ${successes.length} / ${total}. Erros: ${errors.length}.\n\n`;
    message += 'Detalhes dos erros:\n\n';

    Object.keys(errorsByType).forEach(type => {
      const errsOfType = errorsByType[type];
      const typeLabel = this.getErrorTypeLabel(type);
      message += `${typeLabel} (${errsOfType.length}):\n`;

      errsOfType.slice(0, 5).forEach((err) => {
        const userMsg = err.message?.length > 50
          ? err.message.substring(0, 47) + '...'
          : err.message;
        message += `  • ${err.number}: ${userMsg}\n`;
      });

      if (errsOfType.length > 5) {
        message += `  ... e mais ${errsOfType.length - 5} erro(s)\n`;
      }

      message += '\n';
    });

    message += 'Verifique os detalhes completos no console do navegador (F12).';
    return message;
  }

  /**
   * Obter label amigável para tipo de erro
   */
  private getErrorTypeLabel(type: string): string {
    const labels: { [key: string]: string } = {
      'NETWORK_ERROR': 'Erro de Conectividade',
      'CONNECTION_ERROR': 'Falha na Conexão',
      'VALIDATION_ERROR': 'Formato Inválido',
      'AUTH_ERROR': 'Falha de Autenticação',
      'FORBIDDEN_ERROR': 'Acesso Proibido',
      'NOT_FOUND_ERROR': 'Instância Não Encontrada',
      'TIMEOUT_ERROR': 'Tempo Limite Excedido',
      'RATE_LIMIT_ERROR': 'Limite de Requisições',
      'SERVER_ERROR': 'Erro do Servidor',
      'SERVICE_UNAVAILABLE_ERROR': 'Serviço Indisponível',
      'API_ERROR': 'Erro da API',
      'UNKNOWN': 'Erro Desconhecido'
    };
    return labels[type] || labels['UNKNOWN'];
  }

  /**
   * Registrar tentativa de envio no histórico
   */
  private registrarTentativa(
    recipientCount: number,
    successCount: number,
    errorCount: number,
    errors: any[]
  ): void {
    this.sendHistory.push({
      timestamp: new Date(),
      status: errorCount === 0 ? 'success' : (successCount === 0 ? 'error' : 'pending'),
      recipientCount,
      successCount,
      errorCount,
      errors: errorCount > 0 ? errors : undefined
    });

    // Manter apenas últimas 20 tentativas
    if (this.sendHistory.length > 20) {
      this.sendHistory.shift();
    }
  }

  /**
   * Copiar erros para clipboard
   */
  copiarErrosParaClipboard(): void {
    if (this.lastErrors.length === 0) {
      this.confirmService.confirm({
        title: 'Nenhum erro',
        message: 'Não há erros para copiar.',
        icon: 'info',
        disableCancel: true
      });
      return;
    }

    let texto = `Erros de Envio WhatsApp - ${new Date().toLocaleString('pt-BR')}\n`;
    texto += '='.repeat(60) + '\n\n';

    this.lastErrors.forEach((err: any, idx: number): void => {
      texto += `${idx + 1}. Número: ${err.number}\n`;
      texto += `   Tipo: ${err.error?.type || 'UNKNOWN'}\n`;
      texto += `   Mensagem: ${err.error?.message || 'Erro desconhecido'}\n`;
      texto += `   Etapa: ${err.step || 'desconhecida'}\n\n`;
    });

    navigator.clipboard.writeText(texto).then((): void => {
      this.confirmService.confirm({
        title: 'Copiado!',
        message: 'Detalhes dos erros foram copiados para a área de transferência.',
        icon: 'check_circle',
        disableCancel: true
      });
    }).catch((err: any): void => {
      console.error('Erro ao copiar para clipboard:', err);
      this.confirmService.confirm({
        title: 'Erro ao copiar',
        message: 'Falha ao copiar para clipboard. Verifique as permissões do navegador.',
        error: true,
        icon: 'error',
        disableCancel: true
      });
    });
  }

  /**
   * Reenviar apenas as mensagens que falharam
   */
  reenviarFalhas(): void {
    if (this.lastErrors.length === 0) {
      this.confirmService.confirm({
        title: 'Nenhum erro para reenviar',
        message: 'Todas as mensagens foram enviadas com sucesso.',
        icon: 'check_circle',
        disableCancel: true
      });
      return;
    }

    const nums = this.lastErrors.map(e => e.number).join(', ');
    this.confirmService.confirm({
      title: 'Confirmar reenvio',
      message: `Deseja reenviar ${this.lastErrors.length} mensagem(ns) que falharam?\n\nNúmeros: ${nums}`,
      okText: 'Reenviar',
      cancelText: 'Cancelar'
    }).subscribe((confirmed: any) => {
      if (!confirmed) {
        return;
      }

      this.retrying = true;
      this.loader.open('Reenviando mensagens...');

      const currentUser = this.jwtAuth.getUser();
      const username = currentUser?.usuario;
      const message = this.form.value.message;

      let done = 0;
      const retryErrors = [...this.lastErrors];
      const newErrors: any[] = [];
      const newSuccesses: any[] = [];

      const finalizeRetry = (): void => {
        this.loader.close();
        this.retrying = false;

        if (newErrors.length === 0) {
          this.lastErrors = [];
          this.showRetryOption = false;
          this.confirmService.confirm({
            title: 'Reenvio bem-sucedido',
            message: `${newSuccesses.length} mensagem(ns) reenviada(s) com sucesso.`,
            icon: 'check_circle',
            disableCancel: true
          });
        } else {
          this.lastErrors = newErrors;
          const errorSummary = this.formatErrorSummary(newErrors, newSuccesses, retryErrors.length);
          this.confirmService.confirm({
            title: 'Reenvio concluído com erros',
            message: errorSummary,
            error: true,
            icon: 'warning',
            disableCancel: true
          });
        }
      };

      const dataBase = new Date();

      retryErrors.forEach((errItem: any, i: number) => {
        const numero = errItem.number;

        if (this.attachments.length > 0) {
          const baseMediaType = this.attachments[0].type || '';
          const mediatype = baseMediaType.includes('image') ? 'image' :
            baseMediaType.includes('video') ? 'video' : 'document';

          Promise.all(this.attachments.map(file => {
            return new Promise<string>((resolve) => {
              const reader = new FileReader();
              reader.onload = (): void => {
                const result = reader.result as string;
                const base64 = result.split(',')[1] || result;
                resolve(base64);
              };
              reader.readAsDataURL(file);
            });
          })).then((filesBase64: string[]) => {
            const payload: any = {
              number: numero,
              caption: this.mensagemPara(numero, message, dataBase, i),
              files: filesBase64,
              filenames: this.attachments.map(f => f.name),
              mediatype: mediatype
            };

            this.enviarComRetry(username, payload, numero, newSuccesses, newErrors,
              retryErrors.length, (): void => {
                done++;
                if (done === retryErrors.length) {
                  finalizeRetry();
                }
              });
          });
        } else {
          this.enviarComRetry(username, null, numero, newSuccesses, newErrors,
            retryErrors.length, (): void => {
              done++;
              if (done === retryErrors.length) {
                finalizeRetry();
              }
            }, this.mensagemPara(numero, message, dataBase, i));
        }
      });
    });
  }

  /**
   * Envia gerando uma arte por destinatário.
   *
   * Diferente do fluxo comum, aqui o disparo é SEQUENCIAL: cada mensagem exige
   * renderizar um PNG novo, e soltar tudo em paralelo encheria a memória com
   * dezenas de imagens e ainda castigaria a Evolution.
   */
  private enviarComArtePersonalizada(
    username: string,
    toList: string[],
    message: string,
    successes: any[],
    errors: any[],
    finalize: () => void
  ): void {
    const idx = this.indiceArte();
    const modelo = this.modeloArte as ModeloArte;
    // Congela a data-base: uma campanha longa não pode gerar vencimentos
    // diferentes só porque o envio atravessou a meia-noite.
    const dataBase = new Date();

    const outrosBase64 = (): Promise<string[]> => {
      return Promise.all(this.attachments.map((file, i) => {
        if (i === idx) {
          return Promise.resolve('');   // a arte é gerada por destinatário
        }
        return new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = (): void => {
            const result = reader.result as string;
            resolve(result.split(',')[1] || result);
          };
          reader.onerror = (): void => reject(reader.error);
          reader.readAsDataURL(file);
        });
      }));
    };

    Promise.all([
      this.arteService.carregarImagem(this.attachments[idx]),
      outrosBase64()
    ]).then(([imagem, anexosBase64]) => {
      const filenames = this.attachments.map(f => f.name);
      // a arte sai sempre como PNG, mesmo que a original fosse JPG
      filenames[idx] = filenames[idx].replace(/\.[^.]+$/, '') + '.png';

      const enviarUm = (posicao: number): void => {
        if (posicao >= toList.length) {
          finalize();
          return;
        }
        const number = toList[posicao];
        // Número digitado à mão não tem nome no mapa; sem fallback o cupom
        // sairia como "20" e o cabeçalho, vazio.
        const nome = this.nomeNaPosicao(number, posicao) || 'Cliente';
        if (!this.nomeNaPosicao(number, posicao)) {
          console.warn('WhatsApp: sem nome para o número', number, '- usando "Cliente"');
        }
        const ctx = {
          nome,
          desconto: modelo.desconto,
          dataBase,
          prazoDiasUteis: modelo.prazoDiasUteis
        };

        const seguir = (): void => enviarUm(posicao + 1);

        let files: string[];
        try {
          files = anexosBase64.slice();
          files[idx] = this.arteService.renderizarBase64(imagem, modelo.campos, ctx);
        } catch (e) {
          errors.push({ number, step: 'personalizarArte', error: this.extractErrorInfo(e) });
          seguir();
          return;
        }

        const payload: any = {
          number,
          // o texto da mensagem também aceita os mesmos placeholders
          caption: this.arteService.resolver(message, ctx),
          files,
          filenames,
          mediatype: 'image'
        };

        this.whatsappService.sendMedia(username, payload).subscribe({
          next: (): void => {
            successes.push({ number });
            seguir();
          },
          error: (err: any): void => {
            errors.push({ number, step: 'sendMedia', error: this.extractErrorInfo(err) });
            seguir();
          }
        });
      };

      enviarUm(0);
    }).catch((err: any) => {
      errors.push({ number: '-', step: 'prepararArte', error: this.extractErrorInfo(err) });
      finalize();
    });
  }

  /**
   * Enviar com retry automático para erros de rede
   */
  private enviarComRetry(
    username: string,
    payload: any | null,
    numero: string,
    successes: any[],
    errors: any[],
    total: number,
    callback: () => void,
    message?: string
  ): void {
    const maxRetries = 3;
    const baseDelay = 1000;

    const tentarEnvio = (tentativa: number = 0): void => {
      if (payload) {
        this.whatsappService.sendMedia(username, payload).subscribe({
          next: (): void => {
            successes.push({ number: numero });
            callback();
          },
          error: (err: any): void => {
            const errorInfo = this.extractErrorInfo(err);
            const isNetworkError = errorInfo.type === 'NETWORK_ERROR' ||
              errorInfo.type === 'CONNECTION_ERROR' ||
              errorInfo.type === 'TIMEOUT_ERROR';

            if (isNetworkError && tentativa < maxRetries) {
              const delay = baseDelay * Math.pow(2, tentativa);
              console.log(`Tentando novamente em ${delay}ms (tentativa ${tentativa + 1}/${maxRetries})...`);
              setTimeout((): void => {
                tentarEnvio(tentativa + 1);
              }, delay);
            } else {
              errors.push({ number: numero, step: 'sendMedia', error: errorInfo });
              callback();
            }
          }
        });
      } else if (message) {
        this.whatsappService.sendText(username, numero, message).subscribe({
          next: (): void => {
            successes.push({ number: numero });
            callback();
          },
          error: (err: any): void => {
            const errorInfo = this.extractErrorInfo(err);
            const isNetworkError = errorInfo.type === 'NETWORK_ERROR' ||
              errorInfo.type === 'CONNECTION_ERROR' ||
              errorInfo.type === 'TIMEOUT_ERROR';

            if (isNetworkError && tentativa < maxRetries) {
              const delay = baseDelay * Math.pow(2, tentativa);
              console.log(`Tentando novamente em ${delay}ms (tentativa ${tentativa + 1}/${maxRetries})...`);
              setTimeout((): void => {
                tentarEnvio(tentativa + 1);
              }, delay);
            } else {
              errors.push({ number: numero, step: 'sendText', error: errorInfo });
              callback();
            }
          }
        });
      }
    };

    tentarEnvio();
  }

  send(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const toRaw = this.form.value.to;
    const toList: string[] = [];
    const invalidNumbers: string[] = [];

    if (toRaw && typeof toRaw === 'string') {
      const nums = toRaw.split(';').map(num => {
        let n = num.replace(/\D/g, '');
        if (n && !n.startsWith('55')) {
          n = '55' + n;
        }
        if (!/^55\d{2}(9\d{8}|[2-5]\d{7})$/.test(n)) {
          invalidNumbers.push(num.trim());
        }
        return n;
      }).filter(n => n.length > 0);
      toList.push(...nums);
    }

    if (invalidNumbers.length > 0) {
      this.confirmService.confirm({
        title: 'Números inválidos',
        message: `Os seguintes números estão com formato inválido: ${invalidNumbers.join(', ')}`,
        error: true,
        icon: 'warning',
        disableCancel: true
      });
      return;
    }

    const message = this.form.value.message;
    // base do fallback por posição: só vale se a quantidade bater com a lista de origem
    this.totalDestinatarios = toList.length;
    this.sending = true;

    const currentUser = this.jwtAuth.getUser();
    this.instanceName = currentUser?.username || null;
    const username = currentUser?.usuario;

    this.whatsappConfigService.fetchInstance(username).subscribe({
      next: (instance: any): void => {
        let found = false;
        const matchesUsername = (i: any, usernameToCheck: string): boolean => {
          return (
            i !== null &&
            (i.name === usernameToCheck ||
              i.instanceName === usernameToCheck ||
              i.instance?.instanceName === usernameToCheck ||
              i.instance?.name === usernameToCheck)
          );
        };

        if (Array.isArray(instance)) {
          found = instance.some(i => matchesUsername(i, username));
        } else if (instance) {
          found = matchesUsername(instance, username);
        }

        let isConnected = false;
        if (found) {
          const findInst = Array.isArray(instance) ?
            instance.find(i => matchesUsername(i, username)) : instance;
          const instStatus = (findInst?.connectionStatus ||
            findInst?.instance?.connectionStatus ||
            findInst?.instance?.status ||
            findInst?.status)?.toString().toLowerCase();
          if (instStatus === 'open' || instStatus === 'connected') {
            isConnected = true;
          }
        }

        if (!found || !isConnected) {
          this.loader.close();
          this.sending = false;
          this.confirmService.confirm({
            title: 'Instância WhatsApp não conectada',
            message: 'Configure uma instância em Utilitários > Configuração WhatsApp.',
            error: true,
            icon: 'warning',
            disableCancel: true
          });
          return;
        }

        this.loader.open('Enviando...');
        const total = toList.length;

        if (total === 0) {
          this.loader.close();
          this.sending = false;
          this.confirmService.confirm({
            title: 'Nenhum número informado',
            message: 'Informe ao menos um número para envio.',
            error: true,
            icon: 'warning',
            disableCancel: true
          });
          return;
        }

        let done = 0;
        const successes: any[] = [];
        const errors: any[] = [];

        const finalize = (): void => {
          this.loader.close();
          this.sending = false;
          this.registrarTentativa(total, successes.length, errors.length, errors);

          if (errors.length === 0) {
            this.form.reset();
            this.attachments = [];
            this.lastErrors = [];
            this.showRetryOption = false;
            this.confirmService.confirm({
              title: 'Mensagens enviadas',
              message: 'Todas as mensagens foram enviadas com sucesso.',
              icon: 'check_circle',
              disableCancel: true
            });
            this.dialogRef.close({ success: true });
          } else {
            this.lastErrors = errors;
            this.showRetryOption = true;
            const errorSummary = this.formatErrorSummary(errors, successes, total);
            this.confirmService.confirm({
              title: 'Envio concluído com erros',
              message: errorSummary,
              error: true,
              icon: 'warning',
              disableCancel: true
            });
            console.error('Detalhes dos erros de envio:', errors);
          }
        };

        const detectMediaType = (): string | undefined => {
          if (this.attachments.length === 0) {
            return undefined;
          }
          const mime = this.attachments[0].type || '';
          if (mime.startsWith('image/')) {
            return 'image';
          }
          if (mime.startsWith('video/')) {
            return 'video';
          }
          if (mime.startsWith('audio/')) {
            return 'audio';
          }
          return 'document';
        };

        const filesToBase64Once = (): Promise<string[]> => {
          if (this.attachments.length === 0) {
            return Promise.resolve([]);
          }
          return Promise.all(this.attachments.map(file => {
            return new Promise<string>((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = (): void => {
                const result = reader.result as string;
                const base64 = result.split(',')[1] || result;
                resolve(base64);
              };
              reader.onerror = (): void => {
                reject(reader.error);
              };
              reader.readAsDataURL(file);
            });
          }));
        };

        // Arte personalizada: cada destinatário recebe um PNG próprio, então o
        // anexo não pode ser convertido uma vez só e reaproveitado.
        if (this.modeloArte && this.indiceArte() >= 0) {
          this.enviarComArtePersonalizada(username, toList, message, successes, errors, finalize);
          return;
        }

        filesToBase64Once().then((filesBase64: string[]): void => {
          const filenames = this.attachments.map(f => f.name);
          const mediatype = detectMediaType();
          const dataBase = new Date();

          toList.forEach((number: string, i: number): void => {
            if (this.attachments.length > 0) {
              const payload: any = {
                number,
                caption: this.mensagemPara(number, message, dataBase, i),
                files: filesBase64,
                filenames,
                mediatype
              };
              this.whatsappService.sendMedia(username, payload).subscribe({
                next: (): void => {
                  successes.push({ number });
                  done++;
                  if (done === total) {
                    finalize();
                  }
                },
                error: (err: any): void => {
                  errors.push({
                    number,
                    step: 'sendMedia',
                    error: this.extractErrorInfo(err)
                  });
                  done++;
                  if (done === total) {
                    finalize();
                  }
                }
              });
            } else {
              this.whatsappService.sendText(username, number, this.mensagemPara(number, message, dataBase, i)).subscribe({
                next: (): void => {
                  successes.push({ number });
                  done++;
                  if (done === total) {
                    finalize();
                  }
                },
                error: (err: any): void => {
                  errors.push({
                    number,
                    step: 'sendText',
                    error: this.extractErrorInfo(err)
                  });
                  done++;
                  if (done === total) {
                    finalize();
                  }
                }
              });
            }
          });
        }).catch((err: any): void => {
          this.loader.close();
          this.sending = false;
          this.confirmService.confirm({
            title: 'Erro na leitura dos arquivos',
            message: 'Falha ao converter arquivos para base64.',
            error: true,
            icon: 'error',
            disableCancel: true
          });
          console.error('Erro ao converter arquivos:', err);
        });
      },
      error: (): void => {
        this.loader.close();
        this.sending = false;
        this.confirmService.confirm({
          title: 'Falha na verificação',
          message: 'Falha ao verificar instância WhatsApp Evolution.',
          error: true,
          disableCancel: true
        });
      }
    });
  }

  // ── Lookup Texto Adicional ──

  toggleTxtAddLookup(): void {
    this.showTxtAddLookup = !this.showTxtAddLookup;
    if (this.showTxtAddLookup) {
      const lastSearch = localStorage.getItem(WhatsappEditComponent.TXTADD_SEARCH_KEY) || '';
      if (lastSearch && !this.txtAddSearch) {
        this.txtAddSearch = lastSearch;
        this.txtAddSearch$.next(lastSearch);
      } else if (this.txtAddResults.length === 0) {
        this.loadTxtAddPage();
      }
    }
  }

  onTxtAddSearchChange(term: string): void {
    this.txtAddSearch = term;
    this.txtAddSearch$.next(term);
  }

  loadTxtAddPage(): void {
    this.txtAddLoading = true;
    this.txtAddService.getTxtAddPage(1, 20).subscribe(
      (page: any): void => {
        this.txtAddResults = page.content || [];
        this.txtAddLoading = false;
      },
      (): void => {
        this.txtAddLoading = false;
      }
    );
  }

  selectTxtAdd(item: TxtAdd): void {
    const current = this.form.controls['message'].value || '';
    const texto = item.texto || '';
    this.form.controls['message'].setValue(current ? current + '\n' + texto : texto);
    localStorage.setItem(WhatsappEditComponent.TXTADD_SEARCH_KEY, this.txtAddSearch);
    this.showTxtAddLookup = false;
  }

  // ---- Lookup Cadastro (celular do destinatário) ----

  toggleCadastroLookup(): void {
    this.showCadastroLookup = !this.showCadastroLookup;
    if (this.showCadastroLookup) {
      const lastSearch = localStorage.getItem(WhatsappEditComponent.CADASTRO_SEARCH_KEY) || '';
      if (lastSearch && !this.cadastroSearch) {
        this.cadastroSearch = lastSearch;
        this.cadastroSearch$.next(lastSearch);
      } else if (this.cadastroResults.length === 0) {
        this.loadCadastroPage();
      }
    }
  }

  onCadastroSearchChange(term: string): void {
    this.cadastroSearch = term;
    this.cadastroSearch$.next(term);
  }

  loadCadastroPage(): void {
    this.cadastroLoading = true;
    this.cadastroService.getCadastroPageSearch(1, 20, '').subscribe(
      (page: any): void => {
        this.cadastroResults = page?.content || [];
        this.cadastroLoading = false;
      },
      (): void => {
        this.cadastroLoading = false;
      }
    );
  }

  /** Celular do cadastro (com fallback para os telefones), só dígitos. */
  getCadastroFone(item: any): string {
    const fone = item?.celular || item?.telefone1 || item?.telefone2 || '';
    return String(fone).replace(/\D/g, '');
  }

  getCadastroNome(item: any): string {
    return item?.nomeFantasia || item?.razaoSocial || '';
  }

  selectCadastro(item: any): void {
    const numero = this.getCadastroFone(item);
    if (!numero) {
      this.confirmService.confirm({
        title: 'Sem telefone',
        message: `O cadastro "${this.getCadastroNome(item)}" não tem celular/telefone informado.`,
        error: true,
        disableCancel: true
      });
      return;
    }

    // o campo aceita vários destinatários separados por ';' — acrescenta sem duplicar
    const atual = String(this.form.controls['to'].value || '').trim();
    const jaTem = atual.split(';').map(n => n.replace(/\D/g, '')).filter(n => n.length > 0);
    if (jaTem.indexOf(numero) < 0) {
      this.form.controls['to'].setValue(atual ? `${atual};${numero}` : numero);
    }

    localStorage.setItem(WhatsappEditComponent.CADASTRO_SEARCH_KEY, this.cadastroSearch);
    this.showCadastroLookup = false;
  }

  cancel(): void {
    this.dialogRef.close({ success: false, cancelled: true });
  }
}
